import React from 'react';
import { CloudRain, Info, Umbrella } from 'lucide-react';
import { DailySoaringForecast } from '@/lib/mockWeatherData';

interface RainRiskPanelProps {
  forecast: DailySoaringForecast;
}

function rainConfig(probability: number, totalMm: number) {
  if (totalMm >= 5 || probability >= 70) {
    return {
      color: 'var(--danger-red)',
      bg: 'color-mix(in srgb, var(--danger-red) 12%, var(--card))',
      border: 'color-mix(in srgb, var(--danger-red) 34%, var(--card-border))',
    };
  }
  if (totalMm >= 1 || probability >= 35) {
    return {
      color: 'var(--warning-orange)',
      bg: 'color-mix(in srgb, var(--warning-orange) 10%, var(--card))',
      border: 'color-mix(in srgb, var(--warning-orange) 30%, var(--card-border))',
    };
  }
  return {
    color: 'var(--success-green)',
    bg: 'color-mix(in srgb, var(--success-green) 9%, var(--card))',
    border: 'color-mix(in srgb, var(--success-green) 24%, var(--card-border))',
  };
}

export default function RainRiskPanel({ forecast }: RainRiskPanelProps) {
  const probability = forecast.precipProbabilityMax
    ?? Math.max(0, ...(forecast.hourly ?? []).map((h) => h.precipProbability ?? 0));
  const totalMm = forecast.precipTotalMm
    ?? Math.round((forecast.hourly ?? []).reduce((sum, h) => sum + (h.precipAmount ?? 0), 0) * 10) / 10;
  const config = rainConfig(probability, totalMm);
  const label = forecast.rainRiskLabel ?? (probability >= 35 ? 'Averses possibles' : 'Pas de pluie significative');
  const windowLabel = forecast.rainWindowLabel ?? 'Fenetre seche prevue';

  return (
    <div
      className="rounded-xl p-4 transition-all duration-200"
      style={{ backgroundColor: config.bg, border: `1px solid ${config.border}` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CloudRain size={16} style={{ color: config.color }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Pluie / Averses</span>
        </div>
        <span className="font-mono-data text-xl font-bold" style={{ color: config.color }}>
          {probability}%
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>Signal</p>
          <p className="text-sm font-semibold" style={{ color: config.color }}>{label}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>Total jour</p>
          <p className="font-mono-data text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{totalMm.toFixed(1)} mm</p>
        </div>
      </div>

      <div
        className="flex items-start gap-2 p-2.5 rounded-lg"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--surface-elevated) 72%, transparent)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        {probability >= 35 ? (
          <Umbrella size={14} className="flex-shrink-0 mt-0.5" style={{ color: config.color }} />
        ) : (
          <Info size={14} className="flex-shrink-0 mt-0.5" style={{ color: config.color }} />
        )}
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{windowLabel}</p>
      </div>
    </div>
  );
}
